import { useState, useEffect } from 'react';
import '@/App.css';
import axios from 'axios';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import LandingPage from './LandingPage';
import ResultsPage from './ResultsPage';
import QuizV2 from './QuizV2';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_only-creator/artifacts/90vlzise_original%20color%20logo%20%281%29.png';

// Google Analytics Event Tracking
const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

// A/B test: assign each visitor to v1 or v2 (50/50, persisted to localStorage).
// URL override: ?quiz=v1 or ?quiz=v2 forces a specific version (and overwrites the saved one).
// Returning visitors keep the same assignment unless they pass a new URL override.
function resolveQuizVersion() {
  try {
    const params = new URLSearchParams(window.location.search);
    const override = params.get('quiz');
    if (override === 'v1' || override === 'v2') {
      localStorage.setItem('fyo_quiz_version', override);
      return override;
    }
    const stored = localStorage.getItem('fyo_quiz_version');
    if (stored === 'v1' || stored === 'v2') return stored;
    const assigned = Math.random() < 0.5 ? 'v1' : 'v2';
    localStorage.setItem('fyo_quiz_version', assigned);
    return assigned;
  } catch (e) {
    return 'v1';
  }
}

function App() {
  return <MainApp />;
}

function MainApp() {
  const [quizVersion] = useState(resolveQuizVersion);
  const [screen, setScreen] = useState('welcome');
  const [userData, setUserData] = useState({
    firstName: '',
    email: '',
    phone: '',
    answer1: '',
    answer2: '',
    answer3: '',
    answer4: '',
    answer5: '',
    answer6: '',
    answer7: '',
    investment_stage: ''
  });
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [result, setResult] = useState(null);
  const [invalidMessage, setInvalidMessage] = useState('');
  const [questionError, setQuestionError] = useState('');
  const [loading, setLoading] = useState(false);
  const [socialProofCount, setSocialProofCount] = useState(247);
  const [hasRefined, setHasRefined] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [bradQuoteIndex, setBradQuoteIndex] = useState(0);
  const [errors, setErrors] = useState({});
  const [isDownloading, setIsDownloading] = useState(false);
  const [stepHighlight, setStepHighlight] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [statementCopied, setStatementCopied] = useState(false);
  const [statementCopiedMessage, setStatementCopiedMessage] = useState('');
  const [insightsCopied, setInsightsCopied] = useState(false);
  const [linkedinShareCount, setLinkedinShareCount] = useState(0);
  const [igStoryRendering, setIgStoryRendering] = useState(false);
  const [selectedStatement, setSelectedStatement] = useState(null);
  const [showAllStatements, setShowAllStatements] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [resumeChecked, setResumeChecked] = useState(false);
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  const [resumedStep, setResumedStep] = useState(0);
  const [showBookingPopup, setShowBookingPopup] = useState(false);
  const [showAutoSaveBanner, setShowAutoSaveBanner] = useState(false);
  const [showMilestone, setShowMilestone] = useState(false);

  // Per-field input quality validator used on Q1-Q7. Returns null when valid, else an error message.
  const validateAnswerQuality = (raw) => {
    const text = (raw || '').trim();
    if (text.length < 10) return "We need a real answer here to find your ONLY Statement. Even one or two specific sentences will do - just write like you're talking to someone you trust.";
    const tokens = text.split(/\s+/).filter(t => t.length > 1);
    if (tokens.length < 3) return "We need a real answer here to find your ONLY Statement. Even one or two specific sentences will do - just write like you're talking to someone you trust.";
    // Detect single-character repeats (e.g. "aaaaaaaaaa", "..........")
    const nonSpace = text.replace(/\s/g, '');
    if (nonSpace.length > 0) {
      const counts = {};
      for (const ch of nonSpace) counts[ch] = (counts[ch] || 0) + 1;
      const topChar = Math.max(...Object.values(counts));
      if (topChar / nonSpace.length > 0.6) return "We need a real answer here to find your ONLY Statement. Even one or two specific sentences will do - just write like you're talking to someone you trust.";
    }
    // All numeric only
    if (/^[\d\s]+$/.test(text)) return "We need a real answer here to find your ONLY Statement. Even one or two specific sentences will do - just write like you're talking to someone you trust.";
    return null;
  };

  const loadingMessages = [
    'Crafting your category of one...',
    'Finding your $2 bill moment...',
    'Analyzing what makes you unforgettable...',
    'Discovering your ONLY...',
    'Building your unique positioning...',
    'Hollywood storytelling meets AI...'
  ];

  const bradQuotes = [
    "The best TV pitches I ever heard were not the cleverest ones. They were the most specific.",
    "Most people write bios that describe what they do. Your ONLY Statement names what only you can do.",
    "I have sat across from thousands of pitches. The ones that got greenlit were not the best - they were the only."
  ];

  const questions = [
    {
      id: 1,
      title: "What's the most surprising or unexpected part of your background that actually shapes how you work?",
      placeholder: 'e.g., I was a firefighter before becoming a business coach.',
      helper: "Think about what you've done that most people in your industry haven't."
    },
    {
      id: 2,
      title: "What do you do that comes easily to you but seems difficult or unusual to others?",
      placeholder: 'e.g., I can simplify complex strategies into one-page plans.',
      helper: "This is the thing people say about you when you're not in the room."
    },
    {
      id: 3,
      title: 'How do you approach problems differently than most people in your field?',
      placeholder: 'e.g., I help introverts become confident speakers without changing who they are.',
      helper: 'Not just WHAT you solve-HOW you solve it differently.'
    },
    {
      id: 4,
      title: "What's a story from your life or career that captures something essential about who you are?",
      placeholder: 'e.g., I once helped a client 10X their revenue in 90 days using a napkin sketch.',
      helper: 'The memorable moment that makes you unforgettable.'
    },
    {
      id: 5,
      title: 'If you could only be known for one thing, what would it be?',
      placeholder: 'e.g., The person who turns invisible experts into category leaders.',
      helper: "This is the hard one. You can't be everything. What's the ONE thing?"
    },
    {
      id: 6,
      title: "Describe your work and the kind of client you do your best work with.",
      placeholder: "e.g., I'm a brand strategist who works best with ambitious founders ready to stand out.",
      helper: "Be specific about your profession and who you serve."
    },
    {
      id: 7,
      title: "What's the one thing you most want to accomplish in the next 90 days?",
      placeholder: "e.g., Close 10 deals, launch my signature program, grow my email list to 10k.",
      helper: "Short term goals that the ONLY statement should support."
    }
  ];

  // Q8 Investment Stage Qualifier Options (NOT sent to AI)
  const investmentOptions = [
    {
      value: 'cold',
      label: 'Just starting to explore',
      description: "I'm curious but haven't committed to investing yet."
    },
    {
      value: 'warm',
      label: 'Actively working on it',
      description: "I'm putting in effort but need better positioning."
    },
    {
      value: 'hot',
      label: 'Actively investing',
      description: "I'm ready to invest in getting this right and moving fast."
    }
  ];

  const faqItems = [
    {
      question: "What exactly is the $797 ONLY Statement Audit?",
      answer: "The ONLY Statement Audit is a 60-minute 1-on-1 session with Brad Holcman where we go deeper than the AI quiz result. The quiz gives you the first draft. The audit is where the real version gets built. After the session, you receive a 15\u201317 page positioning PDF within 48 hours that includes your refined ONLY Statement, five statement variations, your brand thread, ideal client profile, a 60-second elevator pitch, platform-specific headlines, competitive differentiation, content direction, and strategic recommendations."
    },
    {
      question: "How is this different from the AI result I just received?",
      answer: "The AI result is based entirely on your written answers to eight questions. It is designed to surface the pattern in what you told us. But the real ONLY Statement almost always emerges in conversation. The audit allows Brad to hear the nuance, ask follow-up questions, and identify the deeper pattern in your career that the quiz cannot detect."
    },
    {
      question: "What if I'm not happy with the result?",
      answer: "The goal of the audit is not to land on a single sentence during the call. The real work happens after the session. Brad reviews the conversation and produces the full 15\u201317 page positioning document within 48 hours. This includes five versions of your ONLY Statement, giving you options for how to position yourself depending on context and audience."
    },
    {
      question: "Who is the audit for?",
      answer: "The audit is designed for professionals who already have real expertise but struggle to explain what makes them different. Typical clients include consultants, coaches, founders, executives, real estate investors, and experts building authority in their space. If you already have results but your positioning still sounds generic, the audit is designed for you."
    },
    {
      question: "What happens after the audit?",
      answer: "Some clients simply take the statement and implement it immediately. Others choose to continue into the Authority Accelerator, a 12-week program focused on building an authority platform around the ONLY Statement. If you join the program, the $797 audit fee is credited toward the full investment."
    }
  ];

  // --- Progress Tracking & Session Resumption ---
  const STORAGE_KEY = 'fyonly_progress';

  const saveProgressLocal = (data, step) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        userData: data,
        currentStep: step,
        savedAt: Date.now()
      }));
    } catch (e) {
      console.log('localStorage save failed:', e);
    }
  };

  const clearProgressLocal = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
  };

  const updateProgress = (data, step) => {
    // Save locally
    saveProgressLocal(data, step);
    // Save to backend (non-blocking)
    const answers = {};
    for (let i = 1; i <= 7; i++) {
      if (data[`answer${i}`]) answers[`answer${i}`] = data[`answer${i}`];
    }
    axios.post(`${API}/progress`, {
      email: data.email,
      firstName: data.firstName,
      current_step: step,
      answers: Object.keys(answers).length > 0 ? answers : undefined,
      investment_stage: data.investment_stage || undefined,
      phone: data.phone || undefined,
      quiz_version: quizVersion === 'v2' ? 'v2_redesign' : 'v1_original'
    }).catch(err => console.log('Progress sync failed (non-blocking):', err.message));
  };

  const restoreSession = async () => {
    // 1. Try localStorage first
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Only restore if less than 7 days old
        if (parsed.savedAt && Date.now() - parsed.savedAt < 7 * 24 * 60 * 60 * 1000) {
          const restored = parsed.userData;
          if (restored && restored.email && restored.firstName) {
            setUserData(restored);
            const step = parsed.currentStep || 1;
            setCurrentQuestion(step <= 7 ? step : step);
            setScreen(step === 0 ? 'firstName' : 'questions');
            setResumedStep(step);
            setShowWelcomeBack(true);
            setResumeChecked(true);
            trackEvent('session_resumed', { source: 'localStorage', step });
            return;
          }
        }
      }
    } catch (e) {
      console.log('localStorage restore failed:', e);
    }

    // 2. If no localStorage, check URL params for email-based resume
    try {
      const params = new URLSearchParams(window.location.search);
      const emailParam = params.get('email');
      if (emailParam) {
        const res = await axios.get(`${API}/progress/${encodeURIComponent(emailParam)}`);
        if (res.data && !res.data.completed) {
          const serverData = {
            firstName: res.data.firstName || '',
            email: res.data.email || '',
            answer1: '', answer2: '', answer3: '', answer4: '', answer5: '', answer6: '', answer7: '',
            investment_stage: res.data.investment_stage || '',
            ...(res.data.answers || {})
          };
          setUserData(serverData);
          const step = res.data.current_step || 1;
          setCurrentQuestion(step <= 7 ? step : step);
          setScreen(step === 0 ? 'firstName' : 'questions');
          saveProgressLocal(serverData, step);
          setResumedStep(step);
          setShowWelcomeBack(true);
          setResumeChecked(true);
          trackEvent('session_resumed', { source: 'backend', step });
          return;
        }
      }
    } catch (e) {
      // No saved progress on server, start fresh
    }

    setResumeChecked(true);
  };

  useEffect(() => {
    fetchStats();
    loadFavicon();
    restoreSession();
  }, []);

  const loadFavicon = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const faviconLink = document.getElementById('dynamic-favicon');
      if (faviconLink) {
        faviconLink.href = `${backendUrl}/api/favicon?t=${Date.now()}`;
      }
    } catch (error) {
      console.log('No custom favicon set, using default');
    }
  };

  // Track screen/page views
  useEffect(() => {
    trackEvent('page_view', {
      page_title: screen,
      page_location: window.location.href
    });
    
    // Track results page viewed
    if (screen === 'results') {
      trackEvent('results_page_viewed', {});
      // Fetch LinkedIn share count for social proof under the share button
      axios.get(`${API}/share/count?platform=linkedin&days=7`)
        .then(res => setLinkedinShareCount(res.data?.count || 0))
        .catch(() => {});
    }
  }, [screen]);

  // Show booking popup 5 seconds after results page loads
  useEffect(() => {
    if (screen === 'results') {
      const timer = setTimeout(() => {
        setShowBookingPopup(true);
        trackEvent('booking_popup_triggered', { page: 'results', delay: '15s' });
      }, 15000);
      return () => clearTimeout(timer);
    }
  }, [screen]);

  useEffect(() => {
    if (loading) {
      let index = 0;
      setLoadingMessage(loadingMessages[0]);
      const interval = setInterval(() => {
        index = (index + 1) % loadingMessages.length;
        setLoadingMessage(loadingMessages[index]);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [loading]);

  // Rotate Brad's quotes on the loading screen (separate cadence from the loading messages)
  useEffect(() => {
    if (loading) {
      setBradQuoteIndex(0);
      const interval = setInterval(() => {
        setBradQuoteIndex((i) => (i + 1) % bradQuotes.length);
      }, 4500);
      return () => clearInterval(interval);
    }
  }, [loading]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setSocialProofCount(response.data.todayCount || 247);
    } catch (e) {
      console.error('Failed to fetch stats:', e);
    }
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleStartClick = () => {
    trackEvent('quiz_start', { step: 'welcome', quiz_version: quizVersion });
    if (quizVersion === 'v2') {
      setScreen('quizV2');
      return;
    }
    setScreen('firstName');
    setCurrentQuestion(1);
  };

  const handleFirstNameContinue = async () => {
    const newErrors = {};
    if (!userData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!userData.email.trim()) newErrors.email = 'Email is required';
    else if (!validateEmail(userData.email)) newErrors.email = 'Invalid email format';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setErrors({});
    setLoading(true);
    
    // Send to Make.com webhook (non-blocking - don't wait for response)
    const makeWebhookUrl = process.env.REACT_APP_MAKE_WEBHOOK_URL;
    if (makeWebhookUrl) {
      axios.post(
        makeWebhookUrl,
        {
          name: userData.firstName,
          email: userData.email,
          phone: userData.phone || ''
        },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 5000
        }
      ).catch(error => {
        console.log('Make.com webhook notification failed (non-blocking):', error.message);
      });
    }
    
    // Show success message and proceed to questions
    setErrors({ success: "Success! Let's begin your ONLY discovery." });
    trackEvent('lead_captured', { 
      step: 'email_submitted',
      method: 'quiz_form'
    });
    
    // Track progress after name/email submission
    updateProgress(userData, 0);
    
    setTimeout(() => {
      setErrors({});
      setScreen('questions');
      setCurrentQuestion(1);
      setLoading(false);
      setShowAutoSaveBanner(true);
    }, 1500);
  };

  const handleQuestionContinue = () => {
    // For questions 1-7, validate text input
    if (currentQuestion <= 7) {
      const currentAnswer = userData[`answer${currentQuestion}`];
      const qualityError = validateAnswerQuality(currentAnswer);
      if (qualityError) {
        setQuestionError(qualityError);
        setErrors({ [`answer${currentQuestion}`]: qualityError });
        return;
      }
      setQuestionError('');
      
      // Track question completion
      trackEvent('question_answered', {
        question_number: currentQuestion,
        question_total: 8
      });
      
      setErrors({});
      
      // Trigger step highlight animation
      setStepHighlight(true);
      setTimeout(() => setStepHighlight(false), 400);
      
      const nextQ = currentQuestion + 1;
      
      // Show milestone screen between Q5 and Q6
      if (currentQuestion === 5) {
        setShowMilestone(true);
      } else {
        setCurrentQuestion(nextQ);
      }
      
      // Track progress after each question
      updateProgress(userData, currentQuestion);
    }
  };

  const handleInvestmentSelect = (value) => {
    setUserData({ ...userData, investment_stage: value });
    trackEvent('investment_stage_selected', { stage: value });
  };

  const handleInvestmentContinue = () => {
    if (!userData.investment_stage) {
      setErrors({ investment_stage: 'Please select an option to continue' });
      return;
    }
    setErrors({});

    // Track progress with investment stage (step 8)
    updateProgress(userData, 8);

    // Send qualification data to Make.com webhook (non-blocking)
    const makeWebhookUrl = process.env.REACT_APP_MAKE_WEBHOOK_URL;
    if (makeWebhookUrl) {
      axios.post(
        makeWebhookUrl,
        {
          name: userData.firstName,
          email: userData.email,
          investment_stage: userData.investment_stage,
          tags: ["app-sign-up", userData.investment_stage]
        },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 5000
        }
      ).catch(error => {
        console.log('Make.com qualification webhook failed (non-blocking):', error.message);
      });
    }

    trackEvent('quiz_completed', {});
    handleSubmit();
  };

  const handleQuestionBack = () => {
    if (currentQuestion > 1) {
      setCurrentQuestion(currentQuestion - 1);
    } else {
      setScreen('firstName');
    }
  };

  // ── V2 (A/B redesign) handlers — reuse the existing pipeline. ──
  // Opt-in (after Q1) fires the SAME Make.com webhook + same /api/progress as v1.
  const handleV2OptIn = async ({ firstName: fn, email: em }) => {
    setUserData((prev) => ({ ...prev, firstName: fn, email: em }));
    // Same Make.com webhook v1 fires (phone is empty for v2)
    const makeWebhookUrl = process.env.REACT_APP_MAKE_WEBHOOK_URL;
    if (makeWebhookUrl) {
      axios.post(
        makeWebhookUrl,
        { name: fn, email: em, phone: '' },
        { headers: { 'Content-Type': 'application/json' }, timeout: 5000 }
      ).catch((err) => console.log('Make.com webhook (v2 optin) failed (non-blocking):', err.message));
    }
    // Same /api/progress upsert v1 uses — creates / refreshes user_session
    updateProgress({ firstName: fn, email: em, phone: '' }, 0);
    trackEvent('lead_captured', { step: 'email_submitted', method: 'quiz_v2_optin' });
  };

  // Final submit — set userData with v1-shaped answers, then run the existing pipeline.
  const handleV2Complete = async (payload) => {
    setUserData(payload);
    setLoading(true);
    setScreen('loading');
    trackEvent('quiz_complete', { step: 'all_questions_answered', total_questions: 8, quiz_version: 'v2' });
    try {
      const response = await axios.post(`${API}/generate`, {
        ...payload,
        quiz_version: 'v2_redesign',
      });
      if (response.data?.status === 'INCOMPLETE_INPUT') {
        setInvalidMessage(response.data.message || '');
        setScreen('invalid');
        trackEvent('quiz_invalid_submission', { step: 'generation_rejected', quiz_version: 'v2' });
        return;
      }
      setResult(response.data);
      setScreen('results');
      try { localStorage.removeItem('fyo_quiz_v2_draft'); } catch (e) {}
      trackEvent('results_viewed', { step: 'statement_generated', quiz_version: 'v2' });
    } catch (error) {
      trackEvent('quiz_error', { step: 'generation_failed', quiz_version: 'v2', error: error.response?.data?.detail || 'Unknown error' });
      alert(error.response?.data?.detail || 'Failed to generate statement. Please try again.');
      setScreen('quizV2');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setScreen('loading');
    
    // Track quiz completion
    trackEvent('quiz_complete', {
      step: 'all_questions_answered',
      total_questions: 8
    });
    
    try {
      const response = await axios.post(`${API}/generate`, {
        ...userData,
        quiz_version: quizVersion === 'v2' ? 'v2_redesign' : 'v1_original',
      });

      // Handle AI validation gate - submission flagged as incoherent input
      if (response.data?.status === 'INCOMPLETE_INPUT') {
        setInvalidMessage(response.data.message || '');
        setScreen('invalid');
        trackEvent('quiz_invalid_submission', { step: 'generation_rejected' });
        return;
      }

      setResult(response.data);
      setScreen('results');

      // Clear saved progress on successful completion
      clearProgressLocal();

      // Track results viewed
      trackEvent('results_viewed', {
        step: 'statement_generated'
      });
    } catch (error) {
      trackEvent('quiz_error', {
        step: 'generation_failed',
        error: error.response?.data?.detail || 'Unknown error'
      });
      alert(error.response?.data?.detail || 'Failed to generate statement. Please try again.');
      setScreen('questions');
    } finally {
      setLoading(false);
    }
  };

  const handleRefine = async () => {
    if (hasRefined) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate`, {
        ...userData,
        quiz_version: quizVersion === 'v2' ? 'v2_redesign' : 'v1_original',
      });
      setResult(response.data);
      setHasRefined(true);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to refine statement.');
    } finally {
      setLoading(false);
    }
  };

  // Copy statement to clipboard and show message
  const copyStatementToClipboard = async (platform, customMessage = null) => {
    try {
      const statement = result?.statement || '';
      await navigator.clipboard.writeText(statement);
      
      const message = customMessage || 'Paste your ONLY statement and share it!';
      setStatementCopiedMessage(message);
      setStatementCopied(true);
      trackEvent('share', { method: platform, statement_copied: true });
      
      setTimeout(() => {
        setStatementCopied(false);
        setStatementCopiedMessage('');
      }, 3500);
      
      return true;
    } catch (err) {
      console.error('Failed to copy statement:', err);
      return false;
    }
  };

  // Share handlers for each platform
  const handleShareFacebook = async (e) => {
    e.preventDefault();
    await copyStatementToClipboard('facebook');
    setTimeout(() => {
      window.open('https://www.facebook.com/sharer/sharer.php?u=https://findyouronly.app/', '_blank');
    }, 3000);
  };

  const handleShareLinkedIn = async (e) => {
    e.preventDefault();
    await copyStatementToClipboard('linkedin');
    setTimeout(() => {
      window.open('https://www.linkedin.com/sharing/share-offsite/?url=https://findyouronly.app/', '_blank');
    }, 3000);
  };

  const handleShareX = async (e) => {
    e.preventDefault();
    const statement = result?.statement || '';
    await copyStatementToClipboard('twitter');
    setTimeout(() => {
      const tweetText = encodeURIComponent(`${statement}\n\nDiscover your ONLY statement:`);
      window.open(`https://twitter.com/intent/tweet?text=${tweetText}&url=https://findyouronly.app/`, '_blank');
    }, 3000);
  };

  const handleShareInstagram = async (e) => {
    e.preventDefault();
    await copyStatementToClipboard('instagram', 'Paste your ONLY statement into your Instagram post!');
    setTimeout(() => {
      window.open('https://www.instagram.com/', '_blank');
    }, 3000);
  };

  const handleShareTikTok = async (e) => {
    e.preventDefault();
    await copyStatementToClipboard('tiktok', 'Paste your ONLY statement into your TikTok caption!');
    setTimeout(() => {
      window.open('https://www.tiktok.com/', '_blank');
    }, 3000);
  };

  const handleCopyLink = async () => {
    try {
      const statement = result?.statement || '';
      const textToCopy = `${statement}\n\nDiscover your ONLY statement: https://findyouronly.app/`;
      await navigator.clipboard.writeText(textToCopy);
      setLinkCopied(true);
      setStatementCopiedMessage('Your ONLY statement and link were copied!');
      setStatementCopied(true);
      trackEvent('share', { method: 'copy_link' });
      setTimeout(() => {
        setLinkCopied(false);
        setStatementCopied(false);
        setStatementCopiedMessage('');
      }, 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownloadImage = async () => {
    try {
      setIsDownloading(true);
      
      // Create a dedicated element for the image download
      const downloadContent = document.createElement('div');
      downloadContent.style.cssText = `
        position: fixed;
        left: -9999px;
        top: 0;
        width: 1080px;
        padding: 60px;
        background: linear-gradient(135deg, #F5F5DC 0%, #FFF8DC 100%);
        font-family: 'Inter', sans-serif;
      `;
      
      // Build the content
      downloadContent.innerHTML = `
        <div style="text-align: center; margin-bottom: 50px;">
          <h1 style="font-family: 'Playfair Display', serif; font-size: 48px; color: #2C3E50; margin-bottom: 15px; font-weight: 700;">
            Your ONLY Statement
          </h1>
          <p style="font-size: 18px; color: #4A7C59; font-weight: 600;">
            ${userData.firstName}'s draft positioning
          </p>
        </div>
        
        <div style="background: white; padding: 40px; border-radius: 20px; margin-bottom: 40px; border-left: 6px solid #D4AF37; box-shadow: 0 8px 30px rgba(0,0,0,0.1);">
          <p style="font-family: 'Playfair Display', serif; font-size: 32px; font-style: italic; color: #2C3E50; line-height: 1.6; text-align: center; margin: 0;">
            "${result.statement}"
          </p>
        </div>
        
        <div style="background: white; padding: 35px; border-radius: 20px; margin-bottom: 40px; box-shadow: 0 6px 25px rgba(0,0,0,0.08);">
          <h2 style="font-family: 'Playfair Display', serif; font-size: 28px; color: #4A7C59; margin-bottom: 25px; font-weight: 700;">
            Your Journey to Your ONLY
          </h2>
          ${questions.map((q, idx) => `
            <div style="margin-bottom: 30px;">
              <h3 style="font-size: 18px; color: #4A7C59; margin-bottom: 10px; font-weight: 700;">
                ${idx + 1}. ${q.title}
              </h3>
              <p style="font-size: 16px; color: #2C3E50; line-height: 1.7; margin: 0; padding-left: 15px;">
                ${userData[`answer${idx + 1}`]}
              </p>
            </div>
          `).join('')}
        </div>
        
        <div style="text-align: center; padding-top: 30px; border-top: 2px solid #D4AF37;">
          <p style="font-family: 'Playfair Display', serif; font-size: 16px; color: #2C3E50; opacity: 0.7; font-style: italic; margin: 0;">
            Minted with Brad Holcman | The $2 Bill Brand
          </p>
        </div>
      `;
      
      document.body.appendChild(downloadContent);
      
      // Wait for fonts to load
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Capture as canvas
      const canvas = await html2canvas(downloadContent, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#F5F5DC',
        logging: false,
        width: 1080,
        windowWidth: 1080
      });
      
      // Remove the temporary element
      document.body.removeChild(downloadContent);
      
      // Convert to PNG and trigger download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${userData.firstName}-ONLY-Statement.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        // Track image download
        trackEvent('download', {
          content_type: 'image',
          file_name: `${userData.firstName}-ONLY-Statement.png`
        });
        
        setTimeout(() => {
          setIsDownloading(false);
        }, 800);
      }, 'image/png');
      
    } catch (error) {
      console.error('Image generation error:', error);
      alert('Failed to generate image. Please try again.');
      setIsDownloading(false);
    }
  };

  // Generate and download a real 3-page PDF of the user's ONLY Statement diagnostic
  const handleDownloadPDF = () => {
    try {
      const pdf = new jsPDF({ unit: 'in', format: 'letter' });
      const pageW = 8.5;
      const pageH = 11;
      const margin = 0.5;
      const contentW = pageW - margin * 2;
      const GOLD = [212, 175, 55];
      const GREEN = [31, 61, 44];
      const INK = [44, 62, 80];
      const CREAM = [245, 245, 220];

      const fName = (userData.firstName || 'Your').replace(/[^a-zA-Z0-9-]/g, '');

      const drawFooter = () => {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(74, 124, 89);
        pdf.text(
          'Brad Holcman | The $2 Bill Brand | findyouronly.app/audit',
          pageW / 2,
          pageH - 0.3,
          { align: 'center' }
        );
      };

      const drawHeader = () => {
        // Dark green header band
        pdf.setFillColor(...GREEN);
        pdf.rect(0, 0, pageW, 0.55, 'F');
        // Gold accent line
        pdf.setFillColor(...GOLD);
        pdf.rect(0, 0.55, pageW, 0.04, 'F');
        // Left: brand mark
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        pdf.setTextColor(...GOLD);
        pdf.text('$2 Bill Brand', margin, 0.35);
        // Right: Brad credentials
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7.5);
        pdf.setTextColor(245, 245, 220);
        pdf.text(
          'Brad Holcman | 4x Emmy-Nominated TV Executive | 22 years | $2 billion in content | 100+ hit series',
          pageW - margin,
          0.35,
          { align: 'right' }
        );
      };

      // Render text with word-wrap, returns y after rendering
      const writeWrapped = (text, x, y, maxW, opts = {}) => {
        const { size = 10, font = 'helvetica', style = 'normal', color = INK, lineHeight = 1.45, align = 'left' } = opts;
        pdf.setFont(font, style);
        pdf.setFontSize(size);
        pdf.setTextColor(...color);
        const lines = pdf.splitTextToSize(text, maxW);
        const lh = (size / 72) * lineHeight;
        lines.forEach((line, i) => {
          let drawX = x;
          if (align === 'center') drawX = x + maxW / 2;
          if (align === 'right') drawX = x + maxW;
          pdf.text(line, drawX, y + lh * (i + 0.8), { align });
        });
        return y + lh * lines.length + 0.05;
      };

      const sectionHeading = (text, y) => {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(13);
        pdf.setTextColor(...GREEN);
        pdf.text(text, margin, y);
        // gold underline
        pdf.setDrawColor(...GOLD);
        pdf.setLineWidth(0.02);
        pdf.line(margin, y + 0.06, margin + 1.5, y + 0.06);
        return y + 0.28;
      };

      const observationBlock = (text, y) => {
        // Compute height needed
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9.5);
        const lines = pdf.splitTextToSize(text, contentW - 0.3);
        const lh = (9.5 / 72) * 1.45;
        const blockH = lines.length * lh + 0.18;
        // Background tint
        pdf.setFillColor(251, 247, 232);
        pdf.rect(margin, y, contentW, blockH, 'F');
        // Gold left border
        pdf.setFillColor(...GOLD);
        pdf.rect(margin, y, 0.035, blockH, 'F');
        // Text
        pdf.setTextColor(...INK);
        let ty = y + 0.13;
        lines.forEach((line) => {
          pdf.text(line, margin + 0.15, ty);
          ty += lh;
        });
        return y + blockH + 0.1;
      };

      // ============ PAGE 1 ============
      pdf.setFillColor(...CREAM);
      pdf.rect(0, 0, pageW, pageH, 'F');
      drawHeader();
      let y = 0.95;

      // Title
      pdf.setFont('times', 'bold');
      pdf.setFontSize(20);
      pdf.setTextColor(...INK);
      pdf.text(`${userData.firstName || 'Your'}'s Draft ONLY Statement`, margin, y);
      y += 0.4;

      // Statement box (centered text inside white box with gold border)
      const stmtText = `"${result.statement || ''}"`;
      pdf.setFont('times', 'bolditalic');
      pdf.setFontSize(13);
      const stmtLines = pdf.splitTextToSize(stmtText, contentW - 0.5);
      const stmtLh = (13 / 72) * 1.55;
      const stmtBoxH = stmtLines.length * stmtLh + 0.4;
      pdf.setFillColor(255, 255, 255);
      pdf.rect(margin, y, contentW, stmtBoxH, 'F');
      pdf.setDrawColor(...GOLD);
      pdf.setLineWidth(0.025);
      pdf.rect(margin, y, contentW, stmtBoxH);
      pdf.setTextColor(...INK);
      let sy = y + 0.32;
      stmtLines.forEach((line) => {
        pdf.text(line, pageW / 2, sy, { align: 'center' });
        sy += stmtLh;
      });
      y += stmtBoxH + 0.18;

      // Subtext
      y = writeWrapped(
        "This is a draft. It's pointed in the right direction. Here's what we see in it - and what still needs work.",
        margin,
        y,
        contentW,
        { size: 9.5, style: 'italic', color: [74, 124, 89] }
      );
      y += 0.05;

      // What We're Seeing
      y = sectionHeading("What We're Seeing", y);
      (result.observations || []).forEach((obs) => {
        if (obs) y = observationBlock(obs, y);
      });
      y += 0.05;

      // What's Still Blurry
      if (result.whats_blurry) {
        y = sectionHeading("What's Still Blurry", y);
        y = writeWrapped(result.whats_blurry, margin, y, contentW, { size: 10 });
        y = writeWrapped(
          `The draft is pointed in the right direction. The work that's left is the specific gap above. That's not a flaw - it's the next layer, and it's the part that takes a real conversation to get right.`,
          margin,
          y + 0.04,
          contentW,
          { size: 9.5, style: 'italic', color: [74, 124, 89] }
        );
        y += 0.05;
      }

      // ============ PAGE 2 ============
      pdf.addPage();
      pdf.setFillColor(...CREAM);
      pdf.rect(0, 0, pageW, pageH, 'F');
      drawHeader();
      y = 0.95;

      // Three Directions
      y = sectionHeading("Three Directions Your Statement Could Go", y);
      const variations = [
        ['Version 1 (Primary)', result.statement],
        ['Version 2', result.statement_option_2],
        ['Version 3', result.statement_option_3],
      ];
      variations.forEach(([label, text]) => {
        if (!text) return;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.setTextColor(...GOLD);
        pdf.text(label.toUpperCase(), margin, y);
        y += 0.18;
        y = writeWrapped(`"${text}"`, margin, y, contentW, {
          size: 10.5,
          font: 'times',
          style: 'italic',
        });
        y += 0.1;
      });
      y = writeWrapped(
        "The audit is where we pressure-test each one and find the version that's undeniably true.",
        margin,
        y + 0.02,
        contentW,
        { size: 9.5, style: 'italic', color: [74, 124, 89] }
      );
      y += 0.1;

      // The Natural Next Step (dark green CTA card)
      const ctaY = y;
      const ctaCopy = "The draft is the beginning. The audit is where we finish it. In 60 minutes, I ask the questions the quiz can't ask. I find the pattern you've been living your entire career but never consciously named. Within 48 hours, you get a 15-17 page positioning PDF - your refined ONLY Statement, five variations, your brand thread, your ideal client profile, your 60-second elevator pitch, platform headlines, competitive differentiation, and a 90-day plan.";
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      const ctaLines = pdf.splitTextToSize(ctaCopy, contentW - 0.4);
      const ctaLh = (9 / 72) * 1.55;
      const ctaH = ctaLines.length * ctaLh + 0.95;
      pdf.setFillColor(...GREEN);
      pdf.rect(margin, ctaY, contentW, ctaH, 'F');
      pdf.setDrawColor(...GOLD);
      pdf.setLineWidth(0.025);
      pdf.rect(margin, ctaY, contentW, ctaH);
      // Heading
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(...GOLD);
      pdf.text('The Natural Next Step', margin + 0.2, ctaY + 0.32);
      // Body
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(245, 245, 220);
      let cy = ctaY + 0.55;
      ctaLines.forEach((line) => {
        pdf.text(line, margin + 0.2, cy);
        cy += ctaLh;
      });
      // Pull quote + URL
      pdf.setFont('helvetica', 'bolditalic');
      pdf.setFontSize(9);
      pdf.setTextColor(...GOLD);
      pdf.text('"I hired brand strategists for $5,000 who didn\'t get me to this sentence in a year."', margin + 0.2, cy + 0.05);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.text('findyouronly.app/audit', margin + 0.2, cy + 0.32);
      y = ctaY + ctaH + 0.18;

      // Testimonial
      y = writeWrapped(
        '"I left with affirmation that I was in the right place, and a challenge to spread my wings. Brad was able to help me verbalize and own it. The final document captured all that Brad had pointed out."',
        margin,
        y,
        contentW,
        { size: 9.5, style: 'italic', color: INK }
      );
      y = writeWrapped('- Annette Dahl, Consultant', margin, y + 0.02, contentW, {
        size: 8.5,
        style: 'bold',
        color: [74, 124, 89],
      });
      y += 0.1;

      // Positioning Patterns
      if ((result.positioning_patterns || []).length > 0) {
        y = sectionHeading('Your Positioning Patterns', y);
        y = writeWrapped(
          'Across your 8 answers, here are the threads that kept showing up:',
          margin,
          y,
          contentW,
          { size: 10 }
        );
        (result.positioning_patterns || []).forEach((p) => {
          if (!p) return;
          y = writeWrapped(`-  ${p}`, margin + 0.1, y + 0.02, contentW - 0.1, { size: 10 });
        });
      }

      // ============ PAGE 3 ============
      pdf.addPage();
      pdf.setFillColor(...CREAM);
      pdf.rect(0, 0, pageW, pageH, 'F');
      drawHeader();
      y = 0.95;

      y = sectionHeading('Your Answers - For Reference', y);
      const questions = [
        ["Q1. What's the most surprising or unexpected part of your background?", userData.answer1],
        ["Q2. What do you do that comes easily to you but seems difficult to others?", userData.answer2],
        ["Q3. How do you approach problems differently than most in your field?", userData.answer3],
        ["Q4. What's a story from your life or career that captures something essential about who you are?", userData.answer4],
        ["Q5. If you could only be known for one thing, what would it be?", userData.answer5],
        ["Q6. Describe your work and the kind of client you do your best work with.", userData.answer6],
        ["Q7. What's the one thing you most want to accomplish in the next 90 days?", userData.answer7],
      ];
      const investmentLabel = {
        cold: 'Just starting to explore',
        warm: 'Actively working on it',
        hot: 'Actively investing in it',
      }[userData.investment_stage] || userData.investment_stage || '';
      questions.push([
        'Q8. Where are you right now in building your authority and personal brand?',
        investmentLabel,
      ]);
      questions.forEach(([q, a]) => {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.setTextColor(...GREEN);
        const qLines = pdf.splitTextToSize(q, contentW);
        qLines.forEach((line) => {
          pdf.text(line, margin, y);
          y += 0.18;
        });
        y = writeWrapped(a || 'N/A', margin, y, contentW, { size: 10 });
        y += 0.1;
      });

      // Footers on all pages
      const total = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= total; i++) {
        pdf.setPage(i);
        drawFooter();
      }

      pdf.save(`${fName}-ONLY-Statement.pdf`);
      trackEvent('pdf_downloaded', { method: 'jspdf_inline' });
    } catch (e) {
      console.error('PDF generation failed:', e);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  // Calculate progress: currentQuestion / totalQuestions (8) * 100
  const totalQuestions = 8;
  const progress = (currentQuestion / totalQuestions) * 100;

  return (
    <div className="app" data-testid="app-root">
      {screen === 'welcome' && (
        <LandingPage onStart={handleStartClick} />
      )}

      {screen === 'quizV2' && (
        <QuizV2
          onOptIn={handleV2OptIn}
          onComplete={handleV2Complete}
          onTrack={trackEvent}
        />
      )}

      {screen === 'firstName' && (
        <div className="screen firstName-screen" data-testid="firstName-screen">
          <div className="content-wrapper">
            <div className="progress-container">
              <div className="dollar-progress" data-testid="progress-bar">
                <div className="progress-fill" style={{ width: '0%' }}></div>
                <div className="dollar-icon">$2</div>
              </div>
            </div>
            <h2 className="screen-headline" data-testid="firstName-headline">Let's Get Started</h2>
            <p className="screen-subheadline" data-testid="firstName-subheadline">
              Enter your details below to begin your ONLY Statement journey.
            </p>
            <div className="form-container">
              <div className="form-group">
                <label htmlFor="firstName">First Name</label>
                <input
                  id="firstName"
                  type="text"
                  value={userData.firstName}
                  onChange={(e) => setUserData({ ...userData, firstName: e.target.value })}
                  placeholder="Your first name"
                  className={errors.firstName ? 'error' : ''}
                  data-testid="first-name-input"
                  autoFocus
                />
                {errors.firstName && <span className="error-text" data-testid="first-name-error">{errors.firstName}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  id="email"
                  type="email"
                  value={userData.email}
                  onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                  placeholder="your@email.com"
                  className={errors.email ? 'error' : ''}
                  data-testid="email-input"
                />
                {errors.email && <span className="error-text" data-testid="email-error">{errors.email}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone Number <span className="phone-helper">Optional - for text updates</span></label>
                <input
                  id="phone"
                  type="tel"
                  value={userData.phone || ''}
                  onChange={(e) => setUserData({ ...userData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  data-testid="phone-input"
                />
              </div>
              {errors.success && <div className="success-message" data-testid="success-message">{errors.success}</div>}
              {errors.ghl && <div className="error-message" data-testid="ghl-error">{errors.ghl}</div>}
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
                <button
                  className="cta-button"
                  onClick={handleFirstNameContinue}
                  disabled={!userData.firstName.trim() || !userData.email.trim() || loading}
                  data-testid="continue-button"
                >
                  {loading ? 'SENDING...' : "Let's Start"}
                </button>
              </div>
              <p className="privacy-note" data-testid="privacy-note">
                We respect your inbox. We'll use your info to personalize your ONLY statement and send valuable insights.
              </p>
            </div>
          </div>
        </div>
      )}

      {screen === 'questions' && currentQuestion <= 7 && !showMilestone && (
        <div className="screen questions-screen" data-testid="questions-screen">
          <div className="content-wrapper">
            <div className="progress-container">
              <div className="dollar-progress" data-testid="progress-bar">
                <div 
                  className={`progress-fill`}
                  style={{ width: `${(currentQuestion / 8) * 100}%` }}
                ></div>
                <div className={`dollar-icon ${stepHighlight ? 'step-highlight' : ''}`}>$2</div>
              </div>
            </div>

            {/* Step Dots + Time Estimate */}
            <div className="step-meta-row" data-testid="step-meta">
              <div className="step-dots" data-testid="step-dots">
                {[1,2,3,4,5,6,7,8].map(s => (
                  <span key={s} className={`step-dot${s === currentQuestion ? ' active' : s < currentQuestion ? ' completed' : ''}`} />
                ))}
              </div>
              <span className="time-estimate" data-testid="time-estimate">~{Math.max(1, Math.ceil((8 - currentQuestion) * 0.5))} min left</span>
            </div>

            {/* Auto-Save Banner */}
            {showAutoSaveBanner && (
              <div className="autosave-banner" data-testid="autosave-banner">
                <span>Your progress is being saved! Close this tab anytime - we'll email you a link to pick up exactly where you left off.</span>
                <button className="autosave-dismiss" onClick={() => setShowAutoSaveBanner(false)} aria-label="Dismiss">&times;</button>
              </div>
            )}
            
            {/* Welcome Back Banner */}
            {showWelcomeBack && (
              <div className="welcome-back-banner" data-testid="welcome-back-banner">
                <div className="welcome-back-content">
                  <span className="welcome-back-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                  </span>
                  <div>
                    <strong>Welcome back, {userData.firstName}!</strong>
                    <span className="welcome-back-detail">You're on question {resumedStep <= 7 ? resumedStep : 8} of 8 - pick up right where you left off.</span>
                  </div>
                </div>
                <button className="welcome-back-dismiss" onClick={() => setShowWelcomeBack(false)} data-testid="welcome-back-dismiss" aria-label="Dismiss">&times;</button>
              </div>
            )}

            {/* Section Header for Questions 1-5 */}
            {currentQuestion === 1 && (
              <div className="section-header" data-testid="section-header-positioning">
                <h3 className="section-title">Your Positioning</h3>
                <p className="section-description">Answer these questions to discover your unique category of one</p>
              </div>
            )}
            
            {/* Section Header for Questions 6-7 */}
            {currentQuestion === 6 && (
              <div className="section-header" data-testid="section-header-context">
                <h3 className="section-title">Your Business Context</h3>
                <p className="section-description">Help us understand your market and goals</p>
              </div>
            )}
            
            <div className="question-counter" data-testid="question-counter">Question {currentQuestion} of 8</div>
            <h2 className="screen-headline question-headline-mobile" data-testid="question-title">{questions[currentQuestion - 1].title}</h2>
            <p className="helper-text" data-testid="helper-text">{questions[currentQuestion - 1].helper}</p>

            {/* Hard Question Helper for Q4 & Q5 - inside card */}

            <div className="form-container">
              {(currentQuestion === 4 || currentQuestion === 5) && (
                <div className="hard-question-helper" data-testid="hard-question-helper">
                  A few sentences is all you need.
                </div>
              )}
              <div className="textarea-wrapper">
                <textarea
                  rows="6"
                  value={userData[`answer${currentQuestion}`]}
                  onChange={(e) => {
                    setUserData({ ...userData, [`answer${currentQuestion}`]: e.target.value });
                    if (errors[`answer${currentQuestion}`]) {
                      setErrors((prev) => ({ ...prev, [`answer${currentQuestion}`]: undefined }));
                    }
                  }}
                  onBlur={(e) => {
                    // Show inline quality error after user leaves the field (only if they typed something)
                    const val = (e.target.value || '').trim();
                    if (val.length === 0) return;
                    const err = validateAnswerQuality(val);
                    if (err) {
                      setErrors((prev) => ({ ...prev, [`answer${currentQuestion}`]: err }));
                    }
                  }}
                  placeholder={questions[currentQuestion - 1].placeholder}
                  className={errors[`answer${currentQuestion}`] ? 'error' : ''}
                  data-testid="answer-textarea"
                  maxLength={600}
                />
                <div className="textarea-meta">
                  <span className="answer-guidance" data-testid="answer-guidance">
                    Aim for {[1,2,3].includes(currentQuestion) ? '1–2' : [4,5].includes(currentQuestion) ? '2–4' : '1–3'} sentences
                  </span>
                  <span className="char-counter" data-testid="char-counter">
                    {(userData[`answer${currentQuestion}`] || '').length} / 600
                  </span>
                </div>
              </div>
              {errors[`answer${currentQuestion}`] && (
                <p className="answer-quality-error" data-testid="answer-quality-error">{errors[`answer${currentQuestion}`]}</p>
              )}
              <div className="button-group">
                <button className="back-button" onClick={handleQuestionBack} data-testid="back-button">
                  BACK
                </button>
                <button
                  className="cta-button"
                  onClick={handleQuestionContinue}
                  disabled={!userData[`answer${currentQuestion}`] || userData[`answer${currentQuestion}`].length < 10}
                  data-testid="continue-button"
                >
                  CONTINUE
                </button>
              </div>
              {(!userData[`answer${currentQuestion}`] || userData[`answer${currentQuestion}`].length < 10) && (
                <p className="disabled-hint" data-testid="disabled-hint">Type your answer above to continue</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MIDPOINT MILESTONE - between Q5 and Q6 */}
      {screen === 'questions' && showMilestone && (
        <div className="screen milestone-screen" data-testid="milestone-screen">
          <div className="content-wrapper milestone-content">
            <div className="milestone-medal">&#127941;</div>
            <h1 className="milestone-headline" data-testid="milestone-headline">You're halfway there, {userData.firstName}!</h1>
            <div className="milestone-progress-pill" data-testid="milestone-progress">
              {[1,2,3,4,5,6,7,8].map(s => (
                <span key={s} className={`milestone-segment${s <= 5 ? ' filled' : ''}`} />
              ))}
            </div>
            <p className="milestone-text">You've completed your Unique Foundation. Now let's connect it to your business.</p>
            <p className="milestone-time">~2 minutes to your result</p>
            <button
              className="cta-button"
              data-testid="milestone-continue"
              onClick={() => { setShowMilestone(false); setCurrentQuestion(6); }}
            >
              CONTINUE - LET'S GO
            </button>
          </div>
        </div>
      )}

      {/* Q8 Investment Qualifier - NOT sent to AI */}
      {screen === 'questions' && currentQuestion === 8 && !showMilestone && (
        <div className="screen questions-screen investment-screen" data-testid="investment-screen">
          <div className="content-wrapper">
            <div className="progress-container">
              <div className="dollar-progress" data-testid="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: '87.5%' }}
                ></div>
                <div className="dollar-icon">$2</div>
              </div>
            </div>

            {/* Step Dots + Time */}
            <div className="step-meta-row">
              <div className="step-dots">
                {[1,2,3,4,5,6,7,8].map(s => (
                  <span key={s} className={`step-dot${s === 8 ? ' active' : ' completed'}`} />
                ))}
              </div>
              <span className="time-estimate">~30 sec left</span>
            </div>

            {/* Auto-Save Banner */}
            {showAutoSaveBanner && (
              <div className="autosave-banner" data-testid="autosave-banner-q8">
                <span>Your progress is being saved! Close this tab anytime - we'll email you a link to pick up exactly where you left off.</span>
                <button className="autosave-dismiss" onClick={() => setShowAutoSaveBanner(false)} aria-label="Dismiss">&times;</button>
              </div>
            )}

            {/* Finish Line Banner */}
            <div className="finish-line-banner" data-testid="finish-line-banner">
              One last question - you're almost there!
            </div>
            
            {/* Welcome Back Banner (investment screen) */}
            {showWelcomeBack && (
              <div className="welcome-back-banner" data-testid="welcome-back-banner-q8">
                <div className="welcome-back-content">
                  <span className="welcome-back-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                  </span>
                  <div>
                    <strong>Welcome back, {userData.firstName}!</strong>
                    <span className="welcome-back-detail">Just one more question to go - you're almost there.</span>
                  </div>
                </div>
                <button className="welcome-back-dismiss" onClick={() => setShowWelcomeBack(false)} data-testid="welcome-back-dismiss-q8" aria-label="Dismiss">&times;</button>
              </div>
            )}

            <div className="section-header" data-testid="section-header-qualifier">
              <h3 className="section-title">One more thing - this one matters for what happens next.</h3>
              <p className="section-description">Help us understand where you are in your journey</p>
            </div>
            
            <div className="question-counter" data-testid="question-counter">Question 8 of 8</div>
            <h2 className="screen-headline investment-headline" data-testid="question-title">
              When it comes to building your authority and personal brand, where are you right now?
            </h2>
            
            <div className="investment-options" data-testid="investment-options">
              {investmentOptions.map((option) => (
                <button
                  key={option.value}
                  className={`investment-option ${userData.investment_stage === option.value ? 'selected' : ''}`}
                  onClick={() => handleInvestmentSelect(option.value)}
                  data-testid={`investment-option-${option.value}`}
                >
                  <div className="option-checkbox">
                    {userData.investment_stage === option.value && (
                      <svg className="checkbox-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <div className="option-content">
                    <span className="option-label">{option.label}</span>
                    <span className="option-description">{option.description}</span>
                  </div>
                </button>
              ))}
            </div>
            
            {errors.investment_stage && (
              <span className="error-text" data-testid="investment-error">{errors.investment_stage}</span>
            )}
            
            <div className="button-group">
              <button className="back-button" onClick={() => setCurrentQuestion(7)} data-testid="back-button">
                BACK
              </button>
              <button
                className="cta-button generate-button"
                onClick={handleInvestmentContinue}
                disabled={!userData.investment_stage}
                data-testid="generate-button"
              >
                GENERATE MY ONLY STATEMENT
              </button>
            </div>
            {!userData.investment_stage && (
              <p className="disabled-hint disabled-hint-dark" data-testid="q8-disabled-hint">Select an option above to continue</p>
            )}
          </div>
        </div>
      )}

      {screen === 'loading' && (
        <div className="screen loading-screen" data-testid="loading-screen">
          <div className="loading-logo" data-testid="loading-logo">
            <img src={LOGO_URL} alt="$2 Bill Brand Logo" />
          </div>
          <div className="loading-visual" data-testid="loading-visual">
            <img 
              src="https://customer-assets.emergentagent.com/job_category-of-one/artifacts/dlhnrfoa_FM925_Hex_web_029%20%281%29.jpg" 
              alt="Crafting your statement" 
              className="loading-hero-image"
            />
          </div>
          <p className="loading-message" data-testid="loading-message">
            Brad is clarifying your ONLY Statement - cutting through the noise to find what's true about you<span className="dot"></span>
          </p>
          <p className="brad-loading-quote" data-testid="brad-loading-quote" key={bradQuoteIndex}>
            &ldquo;{bradQuotes[bradQuoteIndex]}&rdquo;
          </p>
          <p className="brad-loading-quote-attr">- Brad Holcman</p>
        </div>
      )}

      {screen === 'results' && result && (
        <ResultsPage
          userData={userData}
          result={result}
          linkedinShareCount={linkedinShareCount}
          onCopyStatement={() => {
            navigator.clipboard.writeText(result.statement);
            setStatementCopied(true);
            setStatementCopiedMessage('Statement copied!');
            setTimeout(() => setStatementCopied(false), 2000);
            trackEvent('share', { method: 'copy_inline' });
          }}
          onDownloadPDF={handleDownloadPDF}
          onShareLinkedIn={() => {
            const post = `I just found my ONLY Statement using Brad Holcman's tool. Here is mine: ${result.statement} Find yours at findyouronly.app #ONLYStatement`;
            const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://findyouronly.app')}&text=${encodeURIComponent(post)}`;
            window.open(url, '_blank');
            trackEvent('share', { method: 'linkedin_inline' });
            axios.post(`${API}/share/track`, {
              platform: 'linkedin',
              firstName: userData.firstName,
              email: userData.email,
            }).catch(() => {});
            setLinkedinShareCount((c) => c + 1);
          }}
          onSaveIGStory={async () => {
            const node = document.getElementById('ig-story-canvas');
            if (!node) return;
            setIgStoryRendering(true);
            try {
              // Wait a frame so the hidden node is fully laid out.
              await new Promise((r) => requestAnimationFrame(r));
              const canvas = await html2canvas(node, {
                width: 1080, height: 1920, scale: 1,
                backgroundColor: '#070d09', useCORS: true, allowTaint: true, logging: false,
              });
              const dataUrl = canvas.toDataURL('image/png');
              const link = document.createElement('a');
              link.href = dataUrl;
              link.download = `ONLY-Statement-${(userData.firstName || 'yours').replace(/\s+/g, '-')}.png`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              trackEvent('share', { method: 'ig_story_download' });
              axios.post(`${API}/share/track`, {
                platform: 'ig_story',
                firstName: userData.firstName,
                email: userData.email,
              }).catch(() => {});
            } catch (e) {
              // Silent failure — keeps the UX clean.
            } finally {
              setIgStoryRendering(false);
            }
          }}
          igStoryRendering={igStoryRendering}
          onAuditClick={(location) => trackEvent('audit_cta_clicked', { location })}
        />
      )}

      {/* Hidden 1080×1920 IG-Story render node — html2canvas captures this when the user taps "IG Story". */}
      {screen === 'results' && result && (
        <div
          id="ig-story-canvas"
          aria-hidden="true"
          style={{
            position: 'fixed',
            top: 0,
            left: '-99999px',
            width: '1080px',
            height: '1920px',
            background:
              'radial-gradient(ellipse at 50% 24%, rgba(197,162,74,0.18) 0%, transparent 55%), linear-gradient(180deg, #0d1810 0%, #070d09 55%, #0d1810 100%)',
            color: '#f2ede0',
            fontFamily: "'DM Sans', system-ui, sans-serif",
            padding: '120px 90px',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            overflow: 'hidden',
            zIndex: -1,
          }}
        >
          {/* Top — eyebrow */}
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                fontWeight: 600,
                fontSize: '22px',
                letterSpacing: '0.42em',
                textTransform: 'uppercase',
                color: '#C5A24A',
                marginBottom: '14px',
              }}
            >
              MY ONLY STATEMENT
            </div>
            <div
              style={{
                width: '120px',
                height: '1px',
                background: 'linear-gradient(to right, transparent, #C5A24A, transparent)',
                margin: '0 auto',
              }}
            />
            <div
              style={{
                marginTop: '24px',
                fontWeight: 400,
                fontSize: '20px',
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'rgba(242,237,224,0.55)',
              }}
            >
              {userData.firstName ? `${userData.firstName} ·` : ''} A Brad Holcman Tool
            </div>
          </div>

          {/* Middle — the statement */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <blockquote
              style={{
                margin: 0,
                padding: '0 20px',
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontStyle: 'italic',
                fontWeight: 500,
                fontSize: result && result.statement && result.statement.length > 260 ? '56px' : '72px',
                lineHeight: 1.28,
                letterSpacing: '-0.005em',
                textAlign: 'center',
                color: '#f2ede0',
                textShadow: '0 4px 32px rgba(0,0,0,0.55)',
                position: 'relative',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: 'block',
                  fontSize: '160px',
                  lineHeight: 0.4,
                  color: 'rgba(197,162,74,0.18)',
                  marginBottom: '28px',
                  fontWeight: 700,
                }}
              >
                &ldquo;
              </span>
              {result && result.statement}
            </blockquote>
          </div>

          {/* Bottom — attribution + URL */}
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: '160px',
                height: '1px',
                background: 'linear-gradient(to right, transparent, #C5A24A, transparent)',
                margin: '0 auto 30px',
              }}
            />
            <div
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontStyle: 'italic',
                fontWeight: 600,
                fontSize: '38px',
                color: '#e6c96e',
                background:
                  'linear-gradient(168deg, #f0de96 0%, #e6c96e 28%, #C5A24A 52%, #8a6828 76%, #d4b45e 100%)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '12px',
              }}
            >
              findyouronly.app
            </div>
            <div
              style={{
                fontWeight: 400,
                fontSize: '18px',
                letterSpacing: '0.34em',
                textTransform: 'uppercase',
                color: 'rgba(242,237,224,0.38)',
              }}
            >
              The $2 Bill Brand
            </div>
          </div>
        </div>
      )}
      {false && screen === 'results' && result && (
        <div className="screen results-screen" data-testid="results-screen-legacy">
          <div className="content-wrapper results-content">

            {/* 1. Headline */}
            <h1 className="results-headline" data-testid="results-headline">
              {userData.firstName}, here's what we found.
            </h1>

            {/* 2. Primary ONLY Statement - large, prominent, centered, in quotes */}
            <div className="statement-card" data-testid="statement-card">
              <p className="statement-text">"{result.statement}"</p>

              {/* 3. Subtext under the statement */}
              <p className="statement-subtext" data-testid="statement-subtext">
                This is your draft. It's pointed in the right direction. Here's what we're seeing underneath it.
              </p>
            </div>

            {/* 4. Three action buttons - Copy, Download PDF, Share */}
            <div className="statement-inline-actions outside" data-testid="statement-inline-actions">
              <button
                className="inline-action-btn"
                data-testid="inline-copy-btn"
                onClick={() => {
                  navigator.clipboard.writeText(result.statement);
                  setStatementCopied(true);
                  setStatementCopiedMessage('Statement copied!');
                  setTimeout(() => setStatementCopied(false), 2000);
                  trackEvent('share', { method: 'copy_inline' });
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                Copy
              </button>
              <button
                className="inline-action-btn"
                data-testid="inline-download-btn"
                onClick={handleDownloadPDF}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download PDF
              </button>
              <button
                className="inline-action-btn"
                data-testid="inline-share-btn"
                onClick={() => {
                  const post = `I just found my ONLY Statement using Brad Holcman's tool. Here is mine: ${result.statement} Find yours at findyouronly.app #ONLYStatement`;
                  const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://findyouronly.app')}&text=${encodeURIComponent(post)}`;
                  window.open(url, '_blank');
                  trackEvent('share', { method: 'linkedin_inline' });
                  // Persist for the rolling weekly social proof count
                  axios.post(`${API}/share/track`, {
                    platform: 'linkedin',
                    firstName: userData.firstName,
                    email: userData.email,
                  }).catch(() => {});
                  setLinkedinShareCount((c) => c + 1);
                }}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 11.001-4.124 2.062 2.062 0 01-.001 4.124zm1.782 13.019H3.555V9h3.564v11.452z"/></svg>
                Share
              </button>
            </div>

            {/* LinkedIn share social proof - only when count is 10 or greater */}
            {linkedinShareCount >= 10 && (
              <p className="linkedin-share-proof" data-testid="linkedin-share-proof">
                {linkedinShareCount.toLocaleString()} {linkedinShareCount === 1 ? 'person' : 'people'} shared this on LinkedIn this week
              </p>
            )}

            {/* 5. What We're Seeing - 3 observations as separated blocks with gold left border */}
            {result.observations && result.observations.length > 0 && (
              <div className="insight-section whats-seeing-section" data-testid="whats-seeing-section">
                <h2>What We're Seeing</h2>
                <ul className="insight-list" data-testid="observations-list">
                  {result.observations.map((obs, idx) => (
                    <li key={idx} data-testid={`observation-${idx + 1}`}>{obs}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* 6. What's Still Blurry - one honest gap */}
            {result.whats_blurry && (
              <div className="insight-section whats-blurry-section" data-testid="whats-blurry-section">
                <h2>What's Still Blurry</h2>
                <p data-testid="whats-blurry-text">{result.whats_blurry}</p>
              </div>
            )}

            {/* 7. Statement Variations - ALL THREE visible by default */}
            <div className="variations-section" data-testid="variations-section">
              <h2 className="variations-heading">Three directions your statement could go</h2>
              <div className="variation-block" data-testid="variation-1">
                <span className="variation-label">Version 1 (Primary)</span>
                <p className="variation-text">"{result.statement}"</p>
              </div>
              {result.statement_option_2 && (
                <div className="variation-block" data-testid="variation-2">
                  <span className="variation-label">Version 2</span>
                  <p className="variation-text">"{result.statement_option_2}"</p>
                </div>
              )}
              {result.statement_option_3 && (
                <div className="variation-block" data-testid="variation-3">
                  <span className="variation-label">Version 3</span>
                  <p className="variation-text">"{result.statement_option_3}"</p>
                </div>
              )}
            </div>

            {/* 8. Sharon Thornburg testimonial */}
            <blockquote className="testimonial-card" data-testid="testimonial-sharon-mid">
              <p className="testimonial-quote">
                "My session with Brad was emotionally changing for me. I have been doubting what my gift is for working with people. He helped me see that I do have something of value to share with others. I felt seen and heard."
              </p>
              <cite className="testimonial-attribution">- Sharon Thornburg, Coach</cite>
            </blockquote>

            {/* 9. Brad authority block - circular photo, name, credential, intro line */}
            <div className="brad-authority-card" data-testid="brad-authority-card">
              <img
                src="https://i.postimg.cc/j236Bz5Y/brad-3.jpg"
                alt="Brad Holcman"
                className="brad-authority-photo"
                data-testid="brad-authority-photo"
              />
              <div className="brad-authority-text">
                <p className="brad-authority-name">Brad Holcman</p>
                <p className="brad-authority-credential">4x Emmy-Nominated TV Executive | 22 years | $2 billion in content | 100+ hit series</p>
              </div>
            </div>

            {/* 10-13. Audit pitch section */}
            <div className="audit-pitch-card" data-testid="audit-pitch-section">
              <h2 className="audit-pitch-headline">The draft is the beginning. The audit is where we finish it.</h2>
              <p className="audit-pitch-body">
                In 60 minutes, I ask the questions the quiz cannot ask. I listen for the thing you said in passing that turns out to be the whole point. I find the pattern you have been living your entire career but never consciously named.
              </p>
              <p className="audit-pitch-body">
                Within 48 hours, you get a 15-17 page positioning PDF - your refined ONLY Statement, five variations, your brand thread, your ideal client profile, your 60-second elevator pitch, platform headlines for LinkedIn and your website bio, competitive differentiation, and a 90-day plan.
              </p>
              <p className="audit-pitch-body audit-pitch-quote">
                People tell me afterward: "I hired brand strategists for $5,000 who did not get me to this sentence in a year."
              </p>

              <p className="audit-pitch-subheading">What You Get</p>
              <ul className="audit-pitch-bullets" data-testid="audit-pitch-bullets">
                <li>60-minute 1-on-1 working session with Brad</li>
                <li>15-17 page positioning PDF delivered within 48 hours</li>
                <li>Refined ONLY Statement plus five variations</li>
                <li>Brand thread, ideal client profile, elevator pitch</li>
                <li>LinkedIn headline, website bio, cold outreach opener</li>
                <li>90-day positioning plan</li>
              </ul>

              <p className="audit-pitch-urgency" data-testid="audit-pitch-urgency">
                4-7 sessions available per week. Most people are booked within 7-10 days of placing their order.
              </p>

              <a
                href="https://findyouronly.app/audit"
                target="_blank"
                rel="noopener noreferrer"
                className="audit-cta-button primary-cta"
                data-testid="audit-cta-primary"
                onClick={() => trackEvent('audit_cta_clicked', { location: 'audit_pitch_section_primary' })}
              >
                See what's next for your positioning
              </a>
            </div>

            {/* 14. "Why $797?" section */}
            <div className="why-pricing-card" data-testid="why-pricing-section">
              <h2 className="why-pricing-heading">Why $797?</h2>
              <p className="why-pricing-body">
                You are getting 60 minutes of the positioning instinct that greenlit over $2 billion in content - applied entirely to one sentence about you. The audit is either the most valuable 60 minutes you spend on your brand this year, or the first chapter of something bigger.
              </p>
            </div>

            {/* 15. Bottom CTA */}
            <a
              href="https://findyouronly.app/audit"
              target="_blank"
              rel="noopener noreferrer"
              className="audit-cta-button bottom-cta"
              data-testid="audit-cta-bottom"
              onClick={() => trackEvent('audit_cta_clicked', { location: 'results_bottom' })}
            >
              Book Your ONLY Statement Audit
            </a>

            {/* 16. Not ready yet footnote */}
            <p className="not-ready-footnote" data-testid="not-ready-footnote">
              Not ready yet? Email us at <a href="mailto:info@findyouronly.app">info@findyouronly.app</a> with any questions.
            </p>

            {/* Statement copied popup (utility) */}
            {insightsCopied && null /* legacy hidden */}

          </div>
        </div>
      )}

      {/* INVALID SUBMISSION SCREEN - shown when the AI flags input as incoherent */}
      {screen === 'invalid' && (
        <div className="screen invalid-screen" data-testid="invalid-screen">
          <div className="logo-container">
            <img src={LOGO_URL} alt="$2 Bill Brand Logo" className="brand-logo" data-testid="invalid-logo" />
          </div>
          <div className="invalid-card" data-testid="invalid-card">
            <div className="invalid-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="56" height="56">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="13"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <h1 className="invalid-headline" data-testid="invalid-headline">
              We couldn't build your ONLY Statement yet.
            </h1>
            <div className="invalid-body" data-testid="invalid-body">
              {invalidMessage.split('\n').filter(line => line.trim().length > 0).map((line, idx) => (
                <p key={idx}>{line}</p>
              ))}
            </div>
            <button
              className="cta-button"
              data-testid="invalid-retake-btn"
              onClick={() => {
                // Reset answers and quiz progress, keep firstName/email/phone pre-filled
                setUserData((prev) => ({
                  ...prev,
                  answer1: '', answer2: '', answer3: '', answer4: '',
                  answer5: '', answer6: '', answer7: '',
                  investment_stage: '',
                }));
                setCurrentQuestion(1);
                setShowMilestone(false);
                setErrors({});
                setQuestionError('');
                setInvalidMessage('');
                setResult(null);
                setScreen('firstName');
                trackEvent('quiz_retake_after_invalid', {});
              }}
            >
              Retake the Quiz
            </button>
            <p className="invalid-footnote" data-testid="invalid-footnote">
              Questions? Email us at <a href="mailto:info@findyouronly.app">info@findyouronly.app</a>
            </p>
          </div>
        </div>
      )}

      {/* Booking Popup - appears 5s after results */}
      {showBookingPopup && screen === 'results' && (
        <div
          className="booking-popup-overlay"
          data-testid="booking-popup-overlay"
          onClick={() => setShowBookingPopup(false)}
        >
          <div className="booking-popup" data-testid="booking-popup" onClick={(e) => e.stopPropagation()}>
            <button className="booking-popup-close" onClick={() => setShowBookingPopup(false)} data-testid="booking-popup-close">&times;</button>
            <h3 className="booking-popup-title">Stop Sounding Like Everyone Else</h3>
            <p className="booking-popup-body">Book your private 1-on-1 ONLY Statement Audit with Brad.</p>
            <p className="booking-popup-body">You'll get one full hour to uncover what makes you different, sharpen your message, and walk away with clearer words people actually remember.</p>
            <p className="booking-popup-pricing">
              <span className="booking-popup-price-new">$797</span>
            </p>
            <a
              href="https://audit.2dollarbillbrand.com/checkout-page"
              className="booking-popup-cta"
              data-testid="booking-popup-cta"
              onClick={() => trackEvent('booking_popup_cta_clicked', { source: 'results_page' })}
            >
              Book My 1-Hour Audit
            </a>
          </div>
        </div>
      )}

      {/* Statement Copied Popup Modal */}
      {statementCopied && (
        <div className="copy-popup-overlay">
          <div className="copy-popup-modal">
            <div className="copy-popup-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
            </div>
            <h3 className="copy-popup-title">Statement Copied!</h3>
            <p className="copy-popup-message">{statementCopiedMessage}</p>
            <div className="copy-popup-loader">
              <div className="copy-popup-loader-bar"></div>
            </div>
            <p className="copy-popup-hint">Opening in a moment...</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;